// /api/reservation — read-only status van planner-floor / rolling-floor.
//
// Gebruikt door ControlsColumn om huidige dynamic-floor in de Backup-reserve
// subline te tonen. Mutaties gaan via /api/admin/override-bulk
// (ems.reservation.horizon_hours, ems.reservation.buffer_factor,
// ems.reservation.use_rolling_floor).

import { getJson } from './client';

/** Eén stap in de floor-ladder: hoeveel %SOC deze stap toevoegt. */
export interface FloorLadderStep {
  key: string;
  label: string;
  /** Bijdrage in %SOC; null voor de totaal-regel. */
  delta_pct: number | null;
  /** Stand van de floor ná deze stap. */
  cumulative_pct: number;
  source?: string;
  note?: string;
  is_total?: boolean;
}

/** Eén factor met zijn gemeten invloed op de floor (één-factor-uit rerun). */
export interface FloorFactor {
  key: string;
  label: string;
  /** Huidige waarde van de factor, al leesbaar geformatteerd door de API. */
  value: string;
  /** Invloed op de floor van nu in %SOC; null = niet meetbaar. */
  delta_now_pct: number | null;
  /** Invloed in het knel-uur uit `peak`; null = niet meetbaar of n.v.t. */
  delta_peak_pct?: number | null;
  /** 'up' verhoogt de floor, 'down' verlaagt hem, 'base' is de bodem. */
  direction: 'up' | 'down' | 'base' | 'info';
  note: string;
}

/**
 * Opbouw van de floor — welke factoren bepalen hem en met hoeveel %SOC.
 * Server-side afleiding in `zbs_ems_monitor/floor_breakdown.py`.
 */
export interface FloorBreakdown {
  available: boolean;
  /** False → deltas zijn niet gemeten (geen forecast, of self-check faalde). */
  measurable?: boolean;
  self_check_ok?: boolean | null;
  reason?: string;
  /** Welke floor bindt: statische config, rolling per-uur, of aggregate. */
  binding?: 'static' | 'rolling' | 'aggregate';
  dynamic_floor_pct?: number;
  effective_floor_pct?: number;
  static_min_export_soc_pct?: number;
  safety_floor_pct?: number;
  soc_max_pct?: number;
  capacity_kwh?: number;
  forecast_available?: boolean;
  use_rolling_floor?: boolean;
  rolling_floor_used?: boolean;
  rolling_floor_min_pct?: number | null;
  rolling_floor_max_pct?: number | null;
  horizon_hours?: number;
  buffer_factor?: number;
  buffer_factor_static?: number;
  adaptive_buffer?: boolean;
  refill_aware_buffer?: boolean;
  mape_pct?: number | null;
  mape_over_pct?: number | null;
  avg_load_kw?: number;
  backup_only_load?: boolean;
  ladder?: FloorLadderStep[];
  /** Het uur in de horizon met de hoogste floor; null bij aflopende curve. */
  peak?: {
    slot_index: number;
    hours_ahead: number;
    hour_utc: string;
    floor_pct: number;
    ladder: FloorLadderStep[];
    note: string;
  } | null;
  factors?: FloorFactor[];
  caveat?: string;
}

export interface ReservationStatus {
  /** Statische min-floor zonder rolling-vec (achterval bij forecast-down) */
  min_floor_pct: number;
  /**
   * Floor die mode-blocker NU leest: rolling[0] indien use_rolling_floor=true,
   * anders min_floor_pct. Identieke semantiek als
   * SocReservation.current_floor_pct in zbs_ems_monitor.ems.reservation.
   */
  current_floor_pct: number;
  /**
   * BH-010 (audit #15 B2-1): de ENE floor die de discharge-gate ECHT toepast =
   * max(static ems.revenue_max.min_export_soc, current_floor_pct). Mirror van
   * RevenueMaxMode.decide (revenue_max.py:121) en
   * EmsEngine._effective_export_floor_pct. Toon dit nummer in UI ipv
   * current_floor_pct — anders ziet de gebruiker een lagere floor terwijl
   * de gate stevig dicht zit.
   */
  effective_floor_pct: number;
  /** Hard-floor uit safety-config (lock-input wordt hierop geclamped) */
  safety_floor_pct: number;
  /** Configured planning-horizon in uren (1..48) */
  horizon_hours: number;
  pv_kwh_horizon: number;
  load_kwh_horizon: number;
  net_drain_kwh: number;
  /**
   * Venster in uren waarover het deficit werkelijk is gesommeerd: van nu tot
   * het eerstvolgende PV-herstel. Gelijk aan `horizon_hours` als er geen
   * herstel in zicht is; `null` als er geen forecast was.
   */
  drain_window_hours: number | null;
  /** Buffer-factor voor forecast-MAPE (1.0..3.0) */
  buffer_factor: number;
  forecast_available: boolean;
  fallback_static_pct: number;
  saldering: boolean;
  avg_load_kw_used: number;
  current_soc: number | null;
  reason: string;
  ev_overlay_active: boolean;
  ev_overlay_kw_per_hour: number[] | null;
  /** True wanneer per-uur rolling-floor live is op deze site */
  use_rolling_floor: boolean;
  rolling_floor_pct_per_hour: number[] | null;
  user_lock_pct: number | null;
  user_lock_expires_at: string | null;
  user_lock_active: boolean;
  /** Opbouw van de floor (techniek-overzicht). Ouder API → undefined. */
  floor_breakdown?: FloorBreakdown;
}

export function fetchReservationStatus(): Promise<ReservationStatus> {
  return getJson<ReservationStatus>('/api/reservation');
}
